// Overview screen — editorial layout with multi-lens support.
//
// Rivers display through whichever lenses (whitewater / fly fishing) apply.
// A multi-select lens chip filter lets users narrow to one lens; by default
// both are on.
//
// Layout:
//   1. LENS CHIPS  + filter bar
//   2. BEST BETS   — top 3 rivers scored across BOTH lenses, each shown
//      as a full hero card with the complete narrative inline.
//   3. ALSO GOOD   — every other runnable/fishable river as compact rows.
//   4. WAIT        — too-low / too-high / drought-stalled rivers.

import type { RiverAnalysis, RiverCategory, Region } from '../../functions/_lib/types';
// charts.ts is imported lazily inside attachSparklines() — P017.
import { statusFor } from '../lib/format';
import { getLenses, toggleLens } from '../lib/mode';
import { REGIONS, getActiveRegions, toggleRegion } from '../lib/regions';
import { getStoredLocation, requestLocation, clearStoredLocation, distanceMi, formatMiles } from '../lib/geolocation';
import { renderRiverCard, renderRiverRow } from './river-card';

type Filters = {
  state: string;
  status: string;
  search: string;
  sortBy: 'fit' | 'distance' | 'percentile' | 'flow' | 'name';
};

let filters: Filters = { state: 'all', status: 'all', search: '', sortBy: 'fit' };

export function renderOverview(
  rivers: RiverAnalysis[],
  onHover: (id: string | null) => void,
): string {
  const lenses = getLenses();
  const activeRegions = getActiveRegions();
  const userLoc = getStoredLocation();

  // Scope to active lenses + active regions.
  const activeCats: RiverCategory[] = [];
  if (lenses.whitewater) activeCats.push('whitewater');
  if (lenses.fly_fishing) activeCats.push('fly_fishing');
  const scoped = rivers.filter(
    (r) =>
      r.categories.some((c) => activeCats.includes(c))
      && (!r.region || activeRegions.has(r.region as Region)),
  );

  const states = Array.from(new Set(scoped.map((r) => r.state))).sort();
  const filtered = applyFilters(scoped, filters, activeCats);

  queueMicrotask(() => {
    void attachSparklines(filtered);
    bindControls(rivers, onHover);
    bindCardHover(onHover);
    bindLensChips(rivers, onHover);
    bindRegionChips(rivers, onHover);
    bindLocationButton(rivers, onHover);
  });

  // Editorial only applies when no narrowing filter is active beyond
  // lenses + regions (those are part of the editorial framing).
  const useEditorial = filters.sortBy === 'fit'
    && filters.search === ''
    && filters.state === 'all'
    && filters.status === 'all';

  return `
    <section class="space-y-3">
      <!-- Lens + location row -->
      <div class="flex flex-wrap items-center gap-2 px-1">
        <div class="lens-chips" role="group" aria-label="Lens filter">
          <button class="lens-chip ${lenses.whitewater ? 'is-on' : ''}" data-lens="whitewater" aria-pressed="${lenses.whitewater}">
            <span>🚣</span><span>Whitewater</span>
          </button>
          <button class="lens-chip ${lenses.fly_fishing ? 'is-on' : ''}" data-lens="fly_fishing" aria-pressed="${lenses.fly_fishing}">
            <span>🎣</span><span>Fly Fishing</span>
          </button>
        </div>
        ${renderLocationButton(userLoc, rivers)}
        <span class="text-xs text-base-content/50 ml-auto whitespace-nowrap">${filtered.length} of ${scoped.length}</span>
      </div>

      <!-- Region chips row -->
      <div class="region-chips" role="group" aria-label="Region filter">
        <button id="regions-all" class="region-chip region-chip-all ${activeRegions.size === REGIONS.length ? 'is-on' : ''}">All regions</button>
        ${REGIONS.map((r) => `
          <button class="region-chip ${activeRegions.has(r.id) ? 'is-on' : ''}" data-region="${r.id}" title="${r.label}" aria-pressed="${activeRegions.has(r.id)}">
            ${r.short}
          </button>
        `).join('')}
      </div>

      <!-- Search + sort row -->
      <div class="flex flex-wrap items-center gap-2 px-1">
        <label class="input input-sm input-bordered flex items-center gap-2 grow max-w-xs">
          <span class="opacity-50">⌕</span>
          <input id="filter-search" type="text" class="grow" placeholder="Search rivers…" aria-label="Search rivers" value="${escapeHtml(filters.search)}" />
        </label>
        <select id="filter-state" class="select select-sm select-bordered" aria-label="Filter by state">
          <option value="all" ${filters.state === 'all' ? 'selected' : ''}>All states</option>
          ${states.map((s) => `<option value="${s}" ${s === filters.state ? 'selected' : ''}>${s}</option>`).join('')}
        </select>
        <select id="filter-status" class="select select-sm select-bordered" aria-label="Filter by condition">
          <option value="all" ${filters.status === 'all' ? 'selected' : ''}>Any condition</option>
          <option value="ideal_runnable" ${filters.status === 'ideal_runnable' ? 'selected' : ''}>Ideal / Runnable</option>
          <option value="high_but_ok" ${filters.status === 'high_but_ok' ? 'selected' : ''}>High but fishable / runnable</option>
          <option value="too_low_high" ${filters.status === 'too_low_high' ? 'selected' : ''}>Wait — too low / too high</option>
        </select>
        <select id="filter-sort" class="select select-sm select-bordered" aria-label="Sort rivers">
          <option value="fit" ${filters.sortBy === 'fit' ? 'selected' : ''}>Best today first</option>
          ${userLoc ? `<option value="distance" ${filters.sortBy === 'distance' ? 'selected' : ''}>Nearest first</option>` : ''}
          <option value="percentile" ${filters.sortBy === 'percentile' ? 'selected' : ''}>Highest %ile</option>
          <option value="flow" ${filters.sortBy === 'flow' ? 'selected' : ''}>Highest flow</option>
          <option value="name" ${filters.sortBy === 'name' ? 'selected' : ''}>Name</option>
        </select>
      </div>

      ${userLoc && filters.sortBy === 'fit' ? renderNearYouSection(scoped, userLoc, onHover) : ''}

      ${
        filtered.length === 0
          ? `<div class="rounded-xl border border-dashed border-base-content/10 p-10 text-center text-base-content/50">No rivers match those filters.</div>`
          : useEditorial
            ? renderEditorial(filtered, activeCats)
            : `<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                ${filtered.map((r) => renderRiverCard(r, primaryLensFor(r, activeCats))).join('')}
              </div>`
      }
    </section>
  `;
}

function renderLocationButton(
  userLoc: ReturnType<typeof getStoredLocation>,
  rivers?: RiverAnalysis[],
): string {
  if (userLoc) {
    // Find the nearest river and use its location as a friendly anchor
    // ("near Spokane River") — no third-party reverse geocoding needed.
    let anchor = '';
    if (rivers && rivers.length > 0) {
      let nearest: { r: RiverAnalysis; mi: number } | null = null;
      for (const r of rivers) {
        const mi = distanceMi(userLoc, { lat: r.lat, lon: r.lon });
        if (!nearest || mi < nearest.mi) nearest = { r, mi };
      }
      if (nearest) {
        const where = nearest.mi < 15 ? `at ${nearest.r.river}` : `near ${nearest.r.river}`;
        anchor = ` · ${where} (${formatMiles(nearest.mi)})`;
      }
    }
    return `<div class="loc-row">
      <span class="loc-active" title="Stored locally; auto-expires in 24h">📍 Location set${escapeHtml(anchor)}</span>
      <button id="loc-clear" class="loc-action" title="Clear stored location">Clear</button>
    </div>`;
  }
  return `<button id="loc-request" class="loc-btn" title="Use browser geolocation to sort rivers by proximity">
    📍 Use my location
  </button>`;
}

function renderNearYouSection(
  rivers: RiverAnalysis[],
  loc: { lat: number; lon: number },
  _onHover: (id: string | null) => void,
): string {
  const lenses = getLenses();
  const activeCats: RiverCategory[] = [];
  if (lenses.whitewater) activeCats.push('whitewater');
  if (lenses.fly_fishing) activeCats.push('fly_fishing');
  const withDistance = rivers
    .map((r) => ({ r, mi: distanceMi(loc, { lat: r.lat, lon: r.lon }) }))
    .sort((a, b) => a.mi - b.mi)
    .slice(0, 5);
  if (withDistance.length === 0) return '';
  return `
    <div class="editorial-section">
      <h2 class="editorial-heading">
        <span class="dot dot-ideal"></span>
        <span>Near You</span>
        <span class="editorial-count">${withDistance.length}</span>
      </h2>
      <div class="river-row-list">
        ${withDistance.map(({ r, mi }) => `
          <a href="#/r/${r.id}" class="river-card river-row" data-id="${r.id}">
            <span class="row-dot" style="background:oklch(78% 0.07 80 / 0.6)"></span>
            <span class="row-lenses">${r.categories.map((c) => `<span class="lens-icon">${c === 'fly_fishing' ? '🎣' : '🚣'}</span>`).join('')}</span>
            <span class="row-name">${escapeHtml(r.river)}</span>
            <span class="row-section">${escapeHtml(r.section)}</span>
            <span class="row-state">${r.state}</span>
            <span class="row-cfs tabular-nums">${formatMiles(mi)}</span>
            <span class="row-pct"></span>
            <span class="row-trend"></span>
          </a>
        `).join('')}
      </div>
    </div>
  `;
}

/** Pick the lens to use for status-color/accent on a card. Prefers a lens
 *  the river is actually in (intersection of active lenses + categories). */
function primaryLensFor(r: RiverAnalysis, activeCats: RiverCategory[]): RiverCategory {
  const applicable = activeCats.filter((c) => r.categories.includes(c));
  return applicable[0] ?? r.categories[0] ?? 'whitewater';
}

function renderEditorial(rivers: RiverAnalysis[], activeCats: RiverCategory[]): string {
  const buckets = bucketRivers(rivers, activeCats);
  // No more "top 3 Best Bets" — when 20+ rivers are all "ideal" in a
  // drought year, picking 3 is arbitrary and hides regional favorites
  // (Spokane redbands, etc.). Show ALL ideal/runnable rivers as the
  // primary list, denser cards. Marginal + Wait stay below as compact rows.
  const todaysPicks = buckets.best;
  const marginal = buckets.high;
  const waitFor = buckets.wait;

  return `
    ${todaysPicks.length > 0 ? `
      <div class="editorial-section">
        <h2 class="editorial-heading">
          <span class="dot dot-ideal"></span>
          <span>Running Well Today</span>
          <span class="editorial-count">${todaysPicks.length}</span>
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          ${todaysPicks.map((r) => renderRiverCard(r, primaryLensFor(r, activeCats))).join('')}
        </div>
      </div>
    ` : ''}

    ${marginal.length > 0 ? `
      <div class="editorial-section">
        <h2 class="editorial-heading">
          <span class="dot dot-good"></span>
          <span>High but fishable / runnable</span>
          <span class="editorial-count">${marginal.length}</span>
        </h2>
        <div class="river-row-list">
          ${marginal.map((r) => renderRiverRow(r, primaryLensFor(r, activeCats))).join('')}
        </div>
      </div>
    ` : ''}

    ${waitFor.length > 0 ? `
      <div class="editorial-section">
        <h2 class="editorial-heading">
          <span class="dot dot-wait"></span>
          <span>Wait for water</span>
          <span class="editorial-count">${waitFor.length}</span>
        </h2>
        <div class="river-row-list">
          ${waitFor.map((r) => renderRiverRow(r, primaryLensFor(r, activeCats))).join('')}
        </div>
      </div>
    ` : ''}
  `;
}

/**
 * Bucketing: best/high/wait based on score = max across active lenses.
 * A dual-cat river takes the max of its whitewater and fly_fishing scores,
 * so a river that's "ideal fishing" gets the same Best Bet treatment as
 * a "runnable whitewater" run.
 */
function bucketRivers(rivers: RiverAnalysis[], activeCats: RiverCategory[]): {
  best: RiverAnalysis[];
  high: RiverAnalysis[];
  wait: RiverAnalysis[];
} {
  const scored = rivers.map((r) => ({ r, score: bestBetScoreAcrossLenses(r, activeCats) }));
  scored.sort((a, b) => b.score - a.score
    || ((a.r.percent_of_median ?? 0) - 100) ** 2
       - ((b.r.percent_of_median ?? 0) - 100) ** 2
    || a.r.river.localeCompare(b.r.river));

  const best: RiverAnalysis[] = [];
  const high: RiverAnalysis[] = [];
  const wait: RiverAnalysis[] = [];
  for (const { r, score } of scored) {
    if (score >= 90) best.push(r);
    else if (score >= 50) high.push(r);
    else wait.push(r);
  }
  return { best, high, wait };
}

function bestBetScoreAcrossLenses(r: RiverAnalysis, activeCats: RiverCategory[]): number {
  const scores: number[] = [];
  for (const cat of activeCats) {
    if (!r.categories.includes(cat)) continue;
    scores.push(bestBetScore(r, cat));
  }
  if (scores.length === 0) return 0;
  return Math.max(...scores);
}

function bestBetScore(r: RiverAnalysis, cat: RiverCategory): number {
  const status = statusFor(r, cat);
  let base = 0;
  if (cat === 'fly_fishing') {
    base = ({ ideal: 100, high_but_fishable: 70, too_low: 20, too_high: 30, unknown: 40 } as Record<string, number>)[status] ?? 0;
  } else {
    base = ({ runnable: 100, high: 75, very_high: 55, too_low: 20, flood: 0, unknown: 40 } as Record<string, number>)[status] ?? 0;
  }
  const trendBonus = r.trend_24h.direction === 'steady' ? 5 : 0;
  return base + trendBonus;
}

function applyFilters(rivers: RiverAnalysis[], f: Filters, activeCats: RiverCategory[]): RiverAnalysis[] {
  let out = rivers;
  if (f.state !== 'all') out = out.filter((r) => r.state === f.state);
  if (f.status !== 'all') {
    out = out.filter((r) => {
      // Bucket the river's BEST status across active lenses, then match.
      for (const cat of activeCats) {
        if (!r.categories.includes(cat)) continue;
        const s = statusFor(r, cat);
        const bucket = statusBucket(s);
        if (bucket === f.status) return true;
      }
      return false;
    });
  }
  if (f.search.trim()) {
    const q = f.search.toLowerCase().trim();
    out = out.filter(
      (r) =>
        r.river.toLowerCase().includes(q) ||
        r.section.toLowerCase().includes(q) ||
        r.state.toLowerCase().includes(q),
    );
  }
  out = out.slice();
  switch (f.sortBy) {
    case 'name': out.sort((a, b) => a.river.localeCompare(b.river)); break;
    case 'flow': out.sort((a, b) => (b.current.cfs ?? -Infinity) - (a.current.cfs ?? -Infinity)); break;
    case 'percentile': out.sort((a, b) => (b.percentile.rank ?? -Infinity) - (a.percentile.rank ?? -Infinity)); break;
    case 'distance': {
      const loc = getStoredLocation();
      if (loc) {
        out.sort((a, b) =>
          distanceMi(loc, { lat: a.lat, lon: a.lon })
          - distanceMi(loc, { lat: b.lat, lon: b.lon }));
      }
      break;
    }
    case 'fit': break; // bucketing handles ordering inside renderEditorial
  }
  return out;
}

function statusBucket(status: string): string {
  if (status === 'ideal' || status === 'runnable') return 'ideal_runnable';
  if (status === 'high_but_fishable' || status === 'high' || status === 'very_high') return 'high_but_ok';
  return 'too_low_high';
}

async function attachSparklines(rivers: RiverAnalysis[]): Promise<void> {
  if (rivers.length === 0) return;
  // Lazy-load ECharts (P017). Vite already chunks echarts separately
  // via manualChunks; this defers the WHEN it downloads — chart code
  // only loads when at least one sparkline actually mounts.
  const { makeSparkline } = await import('../lib/charts');
  for (const r of rivers) {
    const el = document.getElementById(`spark-${r.id}`);
    if (el && r.hydrograph_30d.length > 0) {
      makeSparkline(el, r.hydrograph_30d.slice(-90));
    }
  }
}

function bindControls(rivers: RiverAnalysis[], onHover: (id: string | null) => void): void {
  const search = document.getElementById('filter-search') as HTMLInputElement | null;
  const state = document.getElementById('filter-state') as HTMLSelectElement | null;
  const status = document.getElementById('filter-status') as HTMLSelectElement | null;
  const sort = document.getElementById('filter-sort') as HTMLSelectElement | null;

  const rerender = () => {
    // Capture focus + caret state BEFORE the rerender destroys the DOM.
    // The previous version compared `document.activeElement === search`
    // AFTER the rerender — but by then `search` refers to the (now-
    // detached) old input node, and the active element has reset to
    // <body>, so the check was always false and focus was lost on
    // every keystroke. Capture the id + caret position synchronously
    // here so the new input can restore both.
    const active = document.activeElement as HTMLElement | null;
    const wasInSearch = active?.id === 'filter-search';
    const caretPos = wasInSearch
      ? (active as HTMLInputElement).selectionStart ?? null
      : null;

    filters = {
      state: state?.value ?? 'all',
      status: status?.value ?? 'all',
      search: search?.value ?? '',
      sortBy: (sort?.value as Filters['sortBy']) ?? 'fit',
    };
    const host = document.getElementById('overview-host');
    if (host) host.innerHTML = renderOverview(rivers, onHover);

    if (wasInSearch) {
      const newSearch = document.getElementById('filter-search') as HTMLInputElement | null;
      if (newSearch) {
        newSearch.focus();
        const pos = caretPos ?? newSearch.value.length;
        newSearch.setSelectionRange(pos, pos);
      }
    }
  };

  let searchTimer: number | null = null;
  search?.addEventListener('input', () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = window.setTimeout(rerender, 150);
  });
  state?.addEventListener('change', rerender);
  status?.addEventListener('change', rerender);
  sort?.addEventListener('change', rerender);
}

function bindLensChips(_rivers: RiverAnalysis[], _onHover: (id: string | null) => void): void {
  document.querySelectorAll<HTMLButtonElement>('.lens-chip').forEach((btn) => {
    btn.addEventListener('click', () => {
      const lens = btn.dataset.lens as RiverCategory | undefined;
      if (lens) toggleLens(lens);
    });
  });
}

function bindRegionChips(_rivers: RiverAnalysis[], _onHover: (id: string | null) => void): void {
  document.querySelectorAll<HTMLButtonElement>('.region-chip[data-region]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const region = btn.dataset.region;
      if (region) toggleRegion(region as Region);
    });
  });
  document.getElementById('regions-all')?.addEventListener('click', () => {
    // Reset to all regions — pass empty set; setActiveRegions auto-resets.
    import('../lib/regions').then((m) => m.setActiveRegions(new Set()));
  });
}

function bindLocationButton(rivers: RiverAnalysis[], onHover: (id: string | null) => void): void {
  document.getElementById('loc-request')?.addEventListener('click', async () => {
    const btn = document.getElementById('loc-request') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = '📍 Requesting…'; }
    const loc = await requestLocation();
    if (!loc) {
      if (btn) { btn.disabled = false; btn.textContent = '📍 Location unavailable'; setTimeout(() => { btn.textContent = '📍 Use my location'; }, 2000); }
      return;
    }
    // Re-render overview to surface "Near You" + distance sort option.
    const host = document.getElementById('overview-host');
    if (host) host.innerHTML = renderOverview(rivers, onHover);
  });
  document.getElementById('loc-clear')?.addEventListener('click', () => {
    clearStoredLocation();
    const host = document.getElementById('overview-host');
    if (host) host.innerHTML = renderOverview(rivers, onHover);
  });
}

function bindCardHover(onHover: (id: string | null) => void): void {
  document.querySelectorAll<HTMLElement>('.river-card').forEach((card) => {
    card.addEventListener('mouseenter', () => {
      const id = card.dataset.id;
      if (id) onHover(id);
    });
    card.addEventListener('mouseleave', () => onHover(null));
  });
}

export function resetFilters(): void {
  filters = { state: 'all', status: 'all', search: '', sortBy: 'fit' };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
